<template lang='pug'>
.space-table(ref='table' :class='state')
	.table-wrapper(ref='wrapper' @scroll.passive='updateHeaderTranslation')
		simple-search(
			state='open'
			@search='search'
		)
		table(
			v-if='visibleRows.length > 0'
			:class='state'
		)
			tr(
				ref='header'
				v-for='i in 1'
			)
				td.header(
					v-for='col in cols'
					v-bind:class='[{\
						sorted : sortedColumn == col.key,\
						up     : sortedColumn == col.key && sortDirection == 1,\
						down   : sortedColumn == col.key && sortDirection == -1\
					}, col.columnClass, col.headerClass, `icon-arrow`, { "active" : col.activeSort }, col.ascending ? "up" : "down"]'
				)
					p.header(
						@click='sort( col )'

						:title='col.title'
					) {{ col.title }}
					.sub-header(:title='col.subTitle')
						p {{ col.subTitle }}
						.icon-search(@click='toggleSearch( col.key )' :class='{ searching : queries[col.key] }' v-if='col.searchable')
							component(
								:is='searchComponent'

								:state='searchState[col.key]'
								@click.native.stop='() => {}'

								@close='searchState[col.key] = "closed"'
								@search='search( col.key, $event )'
							)
			tr.row.clickable(
				v-for='( row, rIndex ) in visibleRows'
				:id='row[refKey]'
				:class='{ active : row[refKey] === activeRow }'
				:draggable='draggable'
				@dragstart='setDragData( $event, row )'
			)

				td.cell(
					v-for='( col, cIndex ) in cols'

					@click='emitCellClick( rIndex, col.key )'
					:class='col.columnClass'
				)

					//- regular column item

					p(v-if='!col.component') {{ row[ col.key ] || ( col.default ) }}

					component(
						v-else

						:is='col.component'
						:column='col'
						:row='row'
					)

		p.spacey.no-rows(v-if='noResults') No Results Found

		//* pagination on table
		.pagination(v-if='rows.length > increment')
			.pagination-wrapper
				.arrows

					.icon-beginning(@click='activeIndex = 0')

					.icon-prev(@click='activeIndex === 0 ? activeIndex = 0 : activeIndex -= 1')
					//- .icon-prev(@click='changeIndex( activeIndex, 0, 'minus' )')

					p(
						v-for='(counter, index) in totalSlides + 1'
						:class=' activeIndex === index ? "active" : "" '

						@click='counter === 1 ? activeIndex = 0 : activeIndex = ( counter - 1 )'
					) {{ counter }}

					//- .icon-next(@click='changeIndex( activeIndex, totalSlides, 'add' )')
					.icon-next(@click='activeIndex === totalSlides ? activeIndex = totalSlides : activeIndex += 1')

					.icon-end(@click='goToEnd()')

				.indicator
					p Showing {{ paginationProgress }}

		.loading-text(v-if='state === "loading"')
			p {{ settings.loadingText }}
</template>

<script>
import SimpleSearch from '@/components/SimpleSearch';
import Vue from 'vue';
import { ObjToArray } from '@/lib/utils';

export default {
	name : 'space-table',

	props : {
		'columns' : {
			required : true,
			type     : Object
		},

		'rows' : {
			required : true,
			default  : null
		},

		'defaultEmptyCellText' : {
			default : '-'
		},

		'stickyPosition' : {
			optional : true,
			default  : false,
		},

		'draggable' : {
			type    : Boolean,
			default : false
		},

		'options' : {
			type    : Object,
			default : () => ( {} ),
		},

		'refKey' : {
			type    : String,
			default : '',
		},

		'search-component' : {
			type : Object
		},

		'state' : {
			required : true,
			type     : String,
		},

		'selectedRow' : {
			type : String
		},

		'noResults' : {
			type    : Boolean,
			default : false,
		}

	},

	data : () => ( {
		// topbar UI controllers
		topBarLocked    : false,
		translateTopBar : '',

		// active row
		firstVisibleIndex : 0,
		visibleLength     : 100,

		// column sorts
		sortedColumn  : null,
		sortDirection : -1,
		activeSort    : false,

		searchState : {},
		queries     : {},

		rowsArr     : [],
		activeIndex : 0,
		increment   : 10,
	} ),

	computed : {

		cols() {

			/* Order all of the columns */
			const columns          = ObjToArray( this.columns ).filter( a => !a.hidden );
			const orderedColumns   = [];
			const positionTaken    = {};
			const unorderedColumns = [];

			// first, sort the columns into those with an
			// order and those without
			columns.forEach( ( column ) => {

				if ( column.order !== undefined ) {

					// check if there is a conflict in orders
					// e.g. two columns have the same order
					if ( positionTaken[column.order] ) {
						throw new Error( `Column with order ${column.order} already taken` );
					}

					// mark this position as taken
					// and add it into the ordered columns
					positionTaken[column.order] = true;
					orderedColumns.push( column );

					return; // eslint-disable-line

				}

				// this column has no order, and we'll assign
				// it an order based on the orders of the other
				// columns
				unorderedColumns.push( column );

			} );

			let highestFilledOrder = 0;

			const autoOrderedColumns = unorderedColumns.map( ( a, i ) => { // eslint-disable-line

				// start off looking for an opening at
				// the order of the last order where we
				// know that all orders previous to it
				// are taken
				let order = highestFilledOrder;

				while ( positionTaken[order] ) {

					order++; // eslint-disable-line

				}

				// indicate the now highest filled order
				// and set that to the position of this
				// column
				highestFilledOrder = order;
				a.order            = order; // eslint-disable-line

				positionTaken[order] = true;

				return a;

			} );

			// combine the explicitly ordered columns with
			// the columns which we ordered manually and then
			// sort them
			const sortedColumns = orderedColumns.concat( autoOrderedColumns ).sort( ( a, b ) => { // eslint-disable-line
				return a.order - b.order;
			} );

			// finally, we'll fill in all of the default properties
			// of the columns
			return sortedColumns.map( ( a ) => {

				const defaultColumn = {
					default     : this.defaultEmptyCellText,
					name        : '',
					class       : '',
					columnClass : '',
					style       : {},
					ascending   : true,
				};

				const defaultedColumn = Object.assign( defaultColumn, a );

				if ( defaultedColumn.width === 'min' ) {
					defaultedColumn.class += ' min-width';
				}

				const w = defaultedColumn.width;

				defaultedColumn.style.flex = `${w} ${w} 0`;

				if ( defaultedColumn.type === 'control' || defaultColumn.type === 'icon' ) {
					defaultedColumn.style.flex = '0 0 auto';
				}

				return defaultedColumn;

			} );

		},

		columnsEmpty() {
			return ( Object.keys( this.columns ).length === 0 );
		},

		settings() {

			const defaultOptions = {
				loadingText       : 'loading',
				searchPlaceholder : 'Search',
			};

			return Object.assign( defaultOptions, this.options );

		},

		activeRow() {

			// when a use clicks a row, we'd like the
			// table to just set the active row for us
			// but we'd also like the parent view model
			// to be able to set a new active row for us
			// so we have to have a variable telling us
			// whether to use OUR last row clicked, or the
			// parentVM's last set active row.

			if ( this.activeRowLastSetBy === 'table' && this.lastRowClicked !== null ) {
				return this.lastRowClicked;
			}

			if ( this.activeRowLastSetBy === 'parentVM' ) {
				return this.selectedRow;
			}

			return null;

		},

		totalSlides() {
			return Math.ceil( this.rows.length / this.increment ) - 1;
		},

		visibleRows() {
			// determine the lowest possible row and the number of rows
			// currently being displayed
			const lowerBound = ( this.increment * this.activeIndex );
			const length     = Math.min( this.increment, ( this.rows.length - lowerBound ) );

			// slice the appropriate rows
			return this.rows.slice( lowerBound, lowerBound + length );
		},

		paginationProgress() {

			let totalOnPage = 0;

			if ( this.increment > this.visibleRows.length ) {
				totalOnPage = ( ( ( this.activeIndex ) * this.increment ) + this.visibleRows.length );
			}

			else if ( this.increment * this.activeIndex > this.rows.length ) {
				totalOnPage = this.rows.length;
			}

			else {
				totalOnPage = ( ( this.activeIndex + 1 ) * this.increment );
			}

			return `${this.activeIndex === 0 ? 0 : ( this.activeIndex * this.increment ) + 1}-${totalOnPage} of ${this.rows.length}`;
		}

	},

	watch : {

		rows() {
			// this updates when a user searches
			this.activeIndex = 0;
		}

	},

	methods : {

		changeIndex( index, max, operator ) {

			if ( operator === 'add' ) {
				// eslint-disable-next-line no-unused-expressions
				this.activeIndex === max ? this.activeIndex = max : this.activeIndex += 1;
			}

			// eslint-disable-next-line no-unused-expressions
			this.activeIndex === max ? this.activeIndex = max : this.activeIndex -= 1;
		},

		goToEnd() {
			this.activeIndex = this.totalSlides;
		},

		/* * * * * * * * * *
		 * emitted events  *
		 * * * * * * * * * */

		emitSort( column ) {

			this.$emit( 'sort', column );

		},

		emitCellClick( rIndex, cKey ) {

			// determine data to emit
			const row  = this.rows[rIndex];
			const col  = this.columns[cKey]; // eslint-disable-line
			const cell = {
				key   : cKey,
				value : row[cKey]
			};

			const { refKey } = this;

			// update the activeRow and set
			// activeRowLastSetBy to take precedence over
			// the selectedRow prop
			this.activeRowLastSetBy = 'table';
			this.lastRowClicked     = row[refKey];

			// still emit a cell-clicked idk if anybody even uses
			// this still
			this.$emit( 'row-clicked', row, row[refKey] );
			this.$emit( 'cell-clicked', cell );

		},

		sort( column ) {

			this.cols.forEach( ( col ) => {
				col.activeSort = false; // eslint-disable-line
			} );

			column.ascending  = !column.ascending; // eslint-disable-line
			column.activeSort = true; // eslint-disable-line

			this.$emit( 'sort', column );
		},

		toggleSearch( key ) {
			const { columns } = this;

			if ( columns[key].searchable === false ) {
				return;
			}
			const state = this.searchState[key];

			if ( state === 'open' ) {
				Vue.set( this.searchState, key, 'closed' );
				return;
			}

			Vue.set( this.searchState, key, 'open' );

		},

		search( query ) {
			this.$emit( 'search', query );
		},

		setDragData( e, row ) {

			e.dataTransfer.setData( 'data', JSON.stringify( row ) );

		},
	},

	components : {
		SimpleSearch,
	}
};
</script>

<style scoped lang='scss'>
@import '@/assets/scss/_variables.scss';

.space-table {

	.table-wrapper {
		width: 100%;
		overflow: auto;
		position: relative;
		-webkit-overflow-scrolling: touch !important;
		-webkit-backface-visibility: none;
		backface-visibility: none;

		table {
			border-collapse: collapse;
			min-width: 100%;

			&.loaded {

				tr:nth-child(1) {
					box-shadow: none;
				}
			}

			&.loading {

				tr:nth-child(2) {
					opacity: 0;
					pointer-events: none;
				}
			}

			tr {
				display: table-row;

				&:nth-child(1),
				&:nth-child(2) {
					box-shadow: $defaultBoxShadow;
					background: $darkGrey;
				}

				th {

					&::before,
					&::after {
						right: 10px;
					}
				}

				td {
					display: table-cell;
					padding: 7px 5px;

					&:nth-child(1) {
						padding-left: 15px;
					}

					&:nth-last-child(1) {
						padding-right: 15px!important;
					}

					&.header {
						position: relative;
						overflow: auto;
					}

					p {
						font-size: 14px;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
						color: white;

						&.header {
							cursor: pointer;
							font-size: 18px !important;
							color: $color-primary;
							margin-bottom: 5px !important;
							font-weight: 500;
						}
					}
				}
			}

			.row {

				&:hover,
				&.active {
					background: $secondaryGrey;
				}

				&.active td p {
					color: $color-primary;
					font-weight: 500;
				}
			}

			.loading-text {
				width: 100%;
				height: 100px;
				display: flex;
				align-items: center;
				justify-content: center;
				text-transform: uppercase;
				letter-spacing: 2px;
			}
		}

		.pagination {
			padding-top: 12px;
			position: relative;
			z-index: 2;

			.arrows {
				display: flex;
				flex-flow: row;
				align-items: center;

				> *{
					margin: 5px;
					font-size: 14px;

					&:hover {
						cursor: pointer;
					}
				}

				[class^="icon-"], [class*=" icon-"] {

					&::before {
						color: #A8B4BE;
					}
				}

				p.active {
					background-color: darken( $color-primary, 15% );
					color: white!important;
					padding: 3px;
					height: 25px;
					width: 25px;
					border-radius: 3px;
					text-align: center;
				}
			}

			.indicator {
				display: flex;
				align-items: center;
			}
		}

		.loading-text {
			text-align: center;
			text-transform: uppercase;
			color: white;
			letter-spacing: 2px;
			margin-top: 30px;
		}
	}
}
</style>
